import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { environment } from "@app/../environments/environment";
import { SpinnerService } from "@app/core/spinner/spinner.service";
import { HeaderKey, SessionKey } from "@shared/clickar-constants";
import { AlertMessage } from "@shared/services/alert-message/alert-message.model";
import { AlertMessageService } from "@shared/services/alert-message/alert-message.service";
import { SnackbarService } from "@shared/services/snackbar.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { OAuthService } from "angular-oauth2-oidc";
import { Observable, throwError } from "rxjs";
import { catchError, finalize, tap } from "rxjs/operators";

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
	private isServiceInError: boolean;
	private snackBarService = inject(SnackbarService);
	private oauthService: OAuthService = inject(OAuthService);

	currentRoute: string;
	ignoreCodeList = ["400020", "0"];

	constructor(
		private spinnerService: SpinnerService,
		private errorMsgService: AlertMessageService,
		public activatedRoute: ActivatedRoute,
		public storageService: StorageManagerService,
		public router: Router
	) { }

	private isNoLoaderCall = (req: HttpRequest<any>) => req.headers.get(HeaderKey.NO_SPIN) === "true";

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		this.currentRoute = this.router.url;
		switch (req.method) {
			case "OPTIONS":
				return next.handle(req);
			case "DELETE":
			/* if (req.headers.has(HeaderKey.NO_POPUP)) { // Con l'if non va perché deve ritornare sempre qualcosa
				return this.handleRequest(req, next);
			}
			break; */
			/* Falls through */
			default:
				return this.handleRequest(req, next);
		}
	}

	private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const started = Date.now(),
			reqOpt = this.requestInterceptor(req);
		return next.handle(this.requestOptions(req))
			.pipe(
				catchError((err, caught) => {
					return this.onCatch(err, caught);
				})
			).pipe(
				tap(event => {
					if (event instanceof HttpResponse) {
						const elapsed = Date.now() - started;
						if (!environment.production) {
							console.log(`Request for ${req.urlWithParams} took ${elapsed} ms.`);
						}
						this.onSubscribeSuccess(req.url, event as HttpResponse<any>, req.method === "GET" ? true : reqOpt.noSuccessPopup);
					}
				}, (error: any) => {
					if (error.error === "Error 403: AuthenticationFailed") {
						this.storageService.setSessionItem(SessionKey.USER_LOGOUT, "true");
						window.location.href = "/";
					} else {
						this.onSubscribeError(req.url, error, reqOpt.isWarning, reqOpt.noErrorPopup);
					}
				})
			).pipe(
				finalize(() => {
					this.onFinally(req.url, this.isNoLoaderCall(req));
				})
			);
	}

	/**
	 * Request options.
	 * @param req
	 * @returns {HttpRequest<any>}
	 */
	private requestOptions(req: HttpRequest<any>): HttpRequest<any> {
		const _headers = {};
		for (const k of req.headers.keys()) {
			if (
				![
					HeaderKey.NO_SPIN,
					HeaderKey.WARNING,
					HeaderKey.NO_POPUP,
					HeaderKey.NO_SUCCESS_POPUP,
					HeaderKey.NO_ERROR_POPUP
				]
					.includes(k)
			) {
				_headers[k] = req.headers.get(k);
			}
		}

		/* _headers['Content-Type'] = 'application/json; charset=utf-8';
		_headers['Cache-Control'] = 'no-cache';
		_headers['Cache-Control'] = 'no-store';
		_headers['Pragma'] = 'no-cache';
		_headers['If-Modified-Since'] = '0'; */
		return req.clone(
			{
				params: req.params == null ? new HttpParams() : req.params,
				headers: new HttpHeaders(_headers),
				responseType: req.responseType == null ? "text" : req.responseType,
				// withCredentials: environment.credentials,
				reportProgress: true
			}
		);
	}

	private requestInterceptor(req: HttpRequest<any>): CustomRequestOptions {
		// do something on every http request
		const res: CustomRequestOptions = {
			isWarning: false,
			noSuccessPopup: this.isUrlToHidePopup(req.url),
			noErrorPopup: this.isUrlToHidePopup(req.url)
		};
		if (req?.headers) {
			// if (!req.headers.has(HeaderKey.NO_SPIN) || req.headers.get(HeaderKey.NO_SPIN) !== "true") {
			if (!this.isNoLoaderCall(req)) {
				this.spinnerService.show(`HTTP Interceptor -> ${req.url}`);
			}
			res.isWarning = req.headers.has(HeaderKey.WARNING);
			res.noSuccessPopup = req.headers.has(HeaderKey.NO_POPUP) || req.headers.has(HeaderKey.NO_SUCCESS_POPUP);
			if (req.urlWithParams.includes("/token")) {
				res.noSuccessPopup = true;
			}
			res.noErrorPopup = req.headers.has(HeaderKey.NO_POPUP) || req.headers.has(HeaderKey.NO_ERROR_POPUP);
		} else {
			this.spinnerService.show(`HTTP Interceptor -> ${req.url}`);
		}
		return res;
	}

	private isUrlToHidePopup = (url: string): boolean => url.match("(dcmp/services/document/(.*)/(copy|preview)|dcmp/services/document$)") != null;

	private responseInterceptor(url: string, force = false): void {
		// do something on every http response
		this.spinnerService.hide(`HTTP Interceptor -> ${url}`, force);
	}

	private onCatch(error: any, caught: Observable<any>): Observable<any> {
		const adfs = "adfs/ls/IdpInitiatedSignOn";
		if (!environment.production) {
			console.log("HTTP ERROR: ", error);
		}
		if (error != null && error.error != null && error.status == 403 && error.errorCode == 403000) {
			console.log("token scaduto");
			this.oauthService.initCodeFlow();
			return;
		}
		if (error != null && error.status == 500) {
			this.isServiceInError = true;
		}
		if (error != null && error.error != null && error.status == 0) {
			if (error.status == 0) {
				// window.location.href = "/maintenance";
			} else if (error.error.contains("AuthenticationFailed")) {
				this.storageService.setSessionItem(SessionKey.USER_LOGOUT, "true");
				window.location.href = "/";
			} else {
				document.open();
				document.write(error.error.text); // FIXME: WTF!
				document.close();
			}
		}
		return throwError(error);
	}

	private loadTechInfo(headers: HttpHeaders): void {
		if (sessionStorage && headers) {
			if (headers.has(HeaderKey.TECH_INFO)) {
				sessionStorage.setItem(SessionKey.TECH_INFO, headers.get(HeaderKey.TECH_INFO));
			}
			if (headers.has(HeaderKey.TECH_INFO_BE)) {
				sessionStorage.setItem(SessionKey.TECH_INFO_BE, headers.get(HeaderKey.TECH_INFO_BE));
			}
		}
	}

	private onSubscribeSuccess(url: string, res: HttpResponse<any>, noPopup = false): void {
		if (!environment.production) {
			console.log("HTTP Interceptor: ", JSON.stringify(res));
		}

		this.loadTechInfo(res.headers);
		if (!noPopup) {
			this.responseInterceptor(url, true); // WARN: this is also done in onFinally, after success (here, force === true, there force === false)
			this.snackBarService.show({
				titlePlaceholder: "dialog.success.message",
			});
		}
	}

	private onSubscribeError(url: string, error: HttpErrorResponse, isWarn: boolean, noPopup: boolean): void {
		console.log("Errore: ", JSON.stringify(error));

		let err;
		if (error.status !== 0) {
			try {
				err = (error?.error) ?
					(typeof error.error === "string" ? JSON.parse(error.error) : error.error)
					: null;
			} catch (e) {
				err = {
					errorCode: 500
				};
			}
		}
		const errMsg = new AlertMessage();
		if (this.isServiceInError) {
			errMsg.errorCode = "500";
		} else if (err && err.code != null) {
			errMsg.errorCode = String(err.code);
			errMsg.errorMsg = err.message;
		} else {
			errMsg.errorCode = String(error.status != null ? error.status : 0);
			errMsg.errorMsg = error.statusText;
		}
		this.responseInterceptor(url, true);
		this.loadTechInfo(error.headers);
		if (isWarn) {
			this.errorMsgService.newWarning(errMsg);
		} else {
			this.errorMsgService.newError(errMsg);
			if (!noPopup) {
				this.openErrorPopup(errMsg, error.status === 0, this.ignoreCodeList.includes(errMsg.errorCode));
			}
			/* else if (environment.mobile && error.status === 0) {
				this.router.navigate(["/maintenance"]);
			} */
		}
	}

	private onFinally(url: string, keepSpinner: boolean): void {
		if (!keepSpinner) {
			this.responseInterceptor(url);
		}
	}

	private openErrorPopup(error: AlertMessage, blocking = false, notOpenDialog = false): void {
		console.log("notopen", notOpenDialog);
		console.log("notopen", error);

		if (+error.errorCode == 403000) {
			console.log("Token scaduto");
			this.oauthService.initCodeFlow();
			return;
		}

		if (!notOpenDialog) {
			this.snackBarService.show({
				status: "error",
				titlePlaceholder: `error.${error.errorCode}`,
			});
		}
	}
}

interface CustomRequestOptions {
	isWarning: boolean;
	noSuccessPopup: boolean;
	noErrorPopup: boolean;
}